import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, ShieldAlert, BadgeCheck, History } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import cultivarService from '../../services/cultivar';
import {
  ROYALTY_UNIT_LABELS,
  type CultivarProgram,
  type CultivarProgramPriceVersion,
  type RoyaltyUnit,
} from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

const ROYALTY_UNITS: RoyaltyUnit[] = [
  'per_tree_per_year',
  'per_tonne_produced',
  'per_kg_produced',
  'percent_of_sale',
  'fixed_per_sale',
  'other',
];

interface PublishForm {
  effective_from: string;
  royalty_rate: string;
  royalty_unit: RoyaltyUnit | '';
  currency: string;
  minimum_royalty: string;
  per_tree_fee: string;
  annual_fee: string;
  notes: string;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = (seed?: Partial<PublishForm>): PublishForm => ({
  effective_from: todayISO(),
  royalty_rate: '',
  royalty_unit: '',
  currency: 'AUD',
  minimum_royalty: '',
  per_tree_fee: '',
  annual_fee: '',
  notes: '',
  ...seed,
});

function fmtMoney(value: string | number | null, currency: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return '—';
  return `${currency || 'AUD'} ${num.toFixed(2)}`;
}

function ProgramPriceVersions() {
  const { id } = useParams<{ id: string }>();
  const programId = id ? parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const { hasAccess: canAdmin, loading: licenseLoading } = useModuleLicense('cultivar', 'admin');

  const [program, setProgram] = useState<CultivarProgram | null>(null);
  const [versions, setVersions] = useState<CultivarProgramPriceVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPublish, setShowPublish] = useState(false);
  const [form, setForm] = useState<PublishForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(programId)) return;
    setLoading(true);
    setError(null);
    try {
      const [progResp, versResp] = await Promise.all([
        cultivarService.getProgram(programId),
        cultivarService.listPriceVersions(programId),
      ]);
      if (progResp.success && progResp.data) setProgram(progResp.data as CultivarProgram);
      if (versResp.success && versResp.data) {
        setVersions(versResp.data as CultivarProgramPriceVersion[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load price versions');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const openPublish = () => {
    const current = versions.find((v) => v.effective_to === null);
    setForm(emptyForm({
      royalty_rate: current?.royalty_rate != null ? String(current.royalty_rate) : '',
      royalty_unit: (current?.royalty_unit as RoyaltyUnit) || '',
      currency: current?.currency || 'AUD',
      minimum_royalty: current?.minimum_royalty != null ? String(current.minimum_royalty) : '',
      per_tree_fee: current?.per_tree_fee != null ? String(current.per_tree_fee) : '',
      annual_fee: current?.annual_fee != null ? String(current.annual_fee) : '',
    }));
    setShowPublish(true);
  };

  const submit = async () => {
    if (!Number.isFinite(programId)) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        effective_from: form.effective_from || null,
        currency: form.currency.toUpperCase(),
        notes: form.notes || null,
      };
      if (form.royalty_rate !== '')    payload.royalty_rate    = Number(form.royalty_rate);
      if (form.royalty_unit !== '')    payload.royalty_unit    = form.royalty_unit;
      if (form.minimum_royalty !== '') payload.minimum_royalty = Number(form.minimum_royalty);
      if (form.per_tree_fee !== '')    payload.per_tree_fee    = Number(form.per_tree_fee);
      if (form.annual_fee !== '')      payload.annual_fee      = Number(form.annual_fee);
      const resp = await cultivarService.publishPriceVersion(programId, payload);
      if (!resp.success) throw new Error(resp.error?.message || 'Publish failed');
      setShowPublish(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      setSubmitting(false);
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!canAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Cultivar admin seat required</h3>
              <p className="text-sm text-gray-600">Publishing price versions is restricted to users with a cultivar admin licence seat.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-700">{error || 'Program not found.'}</p>
            <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')} className="mt-3">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to programs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={`Price versions — ${program.name}`}
        subtitle="Historical price book. Orders pin to a version, so rate changes only affect new orders placed after publish."
        icon={<History className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Programs
          </Button>
          <Button onClick={openPublish}>
            <Plus className="w-4 h-4 mr-2" /> Publish new version
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>
        )}

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Version</th>
                  <th className="px-4 py-2">Effective</th>
                  <th className="px-4 py-2">Royalty</th>
                  <th className="px-4 py-2">Per tree</th>
                  <th className="px-4 py-2">Annual fee</th>
                  <th className="px-4 py-2">Min royalty</th>
                  <th className="px-4 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {versions.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No price versions yet.</td></tr>
                )}
                {versions.map((v) => {
                  const isCurrent = v.effective_to === null;
                  return (
                    <tr key={v.id} className={isCurrent ? 'bg-emerald-50/50' : ''}>
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1">
                          v{v.version}
                          {isCurrent && (
                            <span className="inline-flex items-center text-emerald-700 text-xs">
                              <BadgeCheck className="w-3.5 h-3.5 ml-1" /> current
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {v.effective_from} → {v.effective_to ?? 'open'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {v.royalty_rate != null
                          ? `${fmtMoney(v.royalty_rate, v.currency)} ${v.royalty_unit ? ROYALTY_UNIT_LABELS[v.royalty_unit as RoyaltyUnit] : ''}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{fmtMoney(v.per_tree_fee, v.currency)}</td>
                      <td className="px-4 py-3 text-gray-700">{fmtMoney(v.annual_fee, v.currency)}</td>
                      <td className="px-4 py-3 text-gray-700">{fmtMoney(v.minimum_royalty, v.currency)}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={v.notes || ''}>{v.notes || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {showPublish && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold">Publish new price version</h2>
                <p className="text-xs text-gray-500 mt-1">The current version (if any) will be closed with effective_to set to this version's effective_from date.</p>
              </div>
              <div className="px-6 py-4 grid grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="text-gray-700">Effective from</span>
                  <input type="date" value={form.effective_from}
                    onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
                <label className="text-sm">
                  <span className="text-gray-700">Currency</span>
                  <input type="text" maxLength={3} value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                    className="mt-1 w-full border rounded px-2 py-1.5 uppercase" />
                </label>
                <label className="text-sm">
                  <span className="text-gray-700">Royalty rate</span>
                  <input type="number" step="0.0001" min={0} value={form.royalty_rate}
                    onChange={(e) => setForm({ ...form, royalty_rate: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
                <label className="text-sm">
                  <span className="text-gray-700">Royalty unit</span>
                  <select value={form.royalty_unit}
                    onChange={(e) => setForm({ ...form, royalty_unit: e.target.value as RoyaltyUnit | '' })}
                    className="mt-1 w-full border rounded px-2 py-1.5">
                    <option value="">—</option>
                    {ROYALTY_UNITS.map((u) => (
                      <option key={u} value={u}>{ROYALTY_UNIT_LABELS[u]}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-gray-700">Per-tree fee (one-off at sale)</span>
                  <input type="number" step="0.01" min={0} value={form.per_tree_fee}
                    onChange={(e) => setForm({ ...form, per_tree_fee: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
                <label className="text-sm">
                  <span className="text-gray-700">Annual fee (per tree per year)</span>
                  <input type="number" step="0.01" min={0} value={form.annual_fee}
                    onChange={(e) => setForm({ ...form, annual_fee: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
                <label className="text-sm">
                  <span className="text-gray-700">Minimum royalty</span>
                  <input type="number" step="0.01" min={0} value={form.minimum_royalty}
                    onChange={(e) => setForm({ ...form, minimum_royalty: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
                <label className="text-sm col-span-2">
                  <span className="text-gray-700">Notes (visible in audit log)</span>
                  <textarea value={form.notes} rows={2}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
              </div>
              <div className="px-6 py-3 border-t flex justify-end gap-2 bg-gray-50">
                <Button variant="secondary" onClick={() => setShowPublish(false)} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} disabled={submitting}>
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Publish version
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ProgramPriceVersions;
